import { useState, useCallback } from 'react'
import { useReactiveVar } from '@apollo/client'
import { useWallet } from '@solana/wallet-adapter-react'
import { isConnectWalletOpenVar } from 'features/auth/store/dialogs'
import { openPrivyLogin } from 'features/auth-privy/utils/open-privy-login'
import { isWalletsDialogOpenVar } from 'features/wallets/store/dialogs'
import { WalletStateStatus } from 'shared/constants'
import { usePlayWallet } from 'shared/hooks/use-play-wallet'
import { PlayWalletStatus } from 'shared/store/play-wallet'
import { userVar } from 'shared/store/user'
import {
  WalletStateContextProvider,
  type WalletState
} from './wallet-state-context'

const { signInToPlay, connectWallet, linkWallet } = WalletStateStatus

const TITLE_BY_STATUS: Record<WalletStateStatus, string> = {
  [WalletStateStatus.signInToPlay]: 'Sign in to Play',
  [WalletStateStatus.connectWallet]: 'Connect wallet',
  [WalletStateStatus.switchChain]: 'Switch chain',
  [WalletStateStatus.linkWallet]: 'Link wallet'
}

/**
 * What stands between the user and a game transaction. A connected wallet
 * that is not linked to the account blocks the game buttons (DEV-323): the
 * chain only sees addresses, and a play from a foreign wallet would land on
 * an auto-created Anonymous account. A connected linked wallet plays.
 */
function getStatusKey(
  user: boolean,
  isConnected: boolean,
  playWalletStatus: PlayWalletStatus | null
): WalletStateStatus | null {
  if (!user) return signInToPlay
  if (!isConnected) return connectWallet
  if (playWalletStatus === PlayWalletStatus.notLinked) return linkWallet
  return null
}

/** What a game button opens: the Privy sign-in for a guest, then a wallet to connect. */
const openNextStep = (hasUser: boolean, isConnected: boolean) => {
  if (!hasUser) {
    openPrivyLogin('game_button')
    return
  }
  if (!isConnected) isConnectWalletOpenVar(true)
  // NOTE: the connected wallet is not linked to the account
  if (hasUser && isConnected) isWalletsDialogOpenVar(true)
}

export const SolanaWalletStateProvider = ({
  children
}: {
  children: React.ReactNode
}) => {
  const user = useReactiveVar(userVar)
  const [isLoading, setIsLoading] = useState(false)
  const { connected, publicKey } = useWallet()
  const { status: playWalletStatus } = usePlayWallet()

  const isConnected = connected && Boolean(publicKey)
  const walletStateKey = getStatusKey(
    Boolean(user),
    isConnected,
    playWalletStatus
  )

  const handlePrepareWallet = useCallback(async () => {
    setIsLoading(true)
    openNextStep(Boolean(user), isConnected)
    setIsLoading(false)
  }, [user, isConnected])

  const value: WalletState = {
    isReady: walletStateKey === null,
    isChainIdValid: true,
    isConnected,
    title: walletStateKey ? TITLE_BY_STATUS[walletStateKey] : null,
    handlePrepareWallet,
    loading: isLoading,
    walletStateKey
  }

  return (
    <WalletStateContextProvider value={value}>
      {children}
    </WalletStateContextProvider>
  )
}
